import { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import { useParams } from "react-router-dom";
import SelectClassModal from "../../components/modal/SelectClassModel";
import { createAssignment } from "../../types/assignmentType";
import DocumentUpload from "../../components/common/DocumetUpload";
import assignmentApis from "../../api/assignmentApis";
import AddRecipient from "../../components/modal/AddRecipients";
import { useBranchCode } from "../../util/hooks/useBranch";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

type ActivePopup = "class" | "student" | null;

export default function CreateAssignment() {
  const { id } = useParams();
  const branchCode = useBranchCode();
  const [activePopup, setActivePopup] = useState<ActivePopup>(null);
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { subjectEnabled } = useSelector((state: RootState) => state.access);
  const [formData, setFormData] = useState<createAssignment>({
    assignmentTitle: "",
    assignmentDescription: "",
    assignmentDueDate: "",
    attachment: "",
    branchCode,
    studentIds: [],
    classIds: [],
    subjectName: "",
    totalMarks: 0,
    isSubmitted: false,
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "isSubmitted" ? value === "true" : value,
    }));
  };


  useEffect(() => {
    if (!id) return;

    const fetchAssignment = async () => {
      try {
        const res = await assignmentApis.getAssignmentById(id);
        if (!res?.data) return;

        const data = res.data;

        setFormData({
          assignmentTitle: data.assignmentTitle ?? "",
          assignmentDescription: data.assignmentDescription ?? "",
          assignmentDueDate: data.assignmentDueDate
            ? data.assignmentDueDate.split("T")[0]
            : "",
          attachment: data.attachment ?? "",
          branchCode: data.branchCode,

          classIds: data.classes?.map((c) => c.classId) || [],
          studentIds: data.students?.map((s) => s.admissionNumber) || [],
          subjectName: data.subjectName ?? "",
          totalMarks: data.totalMarks ?? 0,
          isSubmitted: data.isSubmitted ?? false,
        });
      } catch (err) {
        console.error("Prefill failed", err);
      }
    };

    fetchAssignment();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      formData.classIds.length === 0 ||
      !formData.assignmentTitle ||
      !formData.assignmentDueDate ||
      !formData.subjectName ||
      !formData.assignmentDescription
    ) {
      toast.error("Please enter all required fields");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        ...formData,
        assignmentDueDate: formData.assignmentDueDate
          ? new Date(formData.assignmentDueDate).toISOString()
          : "",
        totalMarks: Number(formData.totalMarks) || 0,
        isSubmitted: formData.isSubmitted ?? false,
      };

      if (!id) {
        // CREATE
        await assignmentApis.createAssignment(payload);
      } else {
        // UPDATE
        await assignmentApis.updateAssignment({
          assignmentId: id,
          payload,
        });
      }
    } catch {
      // toast already handled
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between">
        <PageHeader showBack />
      </div>

      <form className="bg-white px-10 py-9 rounded-lg flex flex-col gap-7">
        <div className="flex flex-col gap-7">
          <div className="grid md:grid-cols-2 gap-7">
            <CustomSelect
              name="classIds"
              label="Select Class*"
              openAsPopup
              value={formData.classIds.join(", ")}
              options={[]}
              onClick={() => setActivePopup("class")}
              placeholder="Select Class"
              onChange={handleInputChange}
            />
            {activePopup === "class" && (
              <SelectClassModal
                title="Select Class"
                actionLabel="Confirm"
                onClose={() => setActivePopup(null)}
                onAction={(selectedClasses) => {
                  setFormData((prev) => ({
                    ...prev,
                    classIds: selectedClasses.map((c) => c.classId),
                  }));
                  setActivePopup(null);
                }}
              />
            )}

            <CustomSelect
              name="classIds"
              label="Select Recipient"
              value={formData.studentIds.join(", ")}
              onClick={() => setActivePopup("student")}
              options={[]}
              openAsPopup
              placeholder="Select recipient"
              onChange={handleInputChange}
            />

            {activePopup === "student" && (
              <AddRecipient
                onClose={(data) => {
                  if (data?.selectedStudentIds) {
                    setFormData((prev) => ({
                      ...prev,
                      studentIds: data.selectedStudentIds,
                    }));
                  }
                  setActivePopup(null);
                }}
              />
            )}

            <CustomInput
              name="assignmentTitle"
              label="Assignment Title*"
              value={formData.assignmentTitle}
              placeholder="Assignment Title"
              onChange={handleInputChange}
            />

            <CustomInput
              name="assignmentDueDate"
              label="Due Date*"
              type="date"
              value={formData.assignmentDueDate}
              onChange={handleInputChange}
            />

            <CustomSelect
              name="subjectName"
              label="Select Subject*"
              value={formData.subjectName}
              placeholder="Select Subject"
              onChange={handleInputChange}
              options={subjectEnabled?.map((subject) => ({
                label: subject.subjectName,
                value: subject.subjectName,
              }))}
            />

            <CustomInput
              name="totalMarks"
              label="Total Marks*"
              value={formData.totalMarks.toString()}
              placeholder="Enter total marks"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  totalMarks: Number(e.target.value),
                }))
              }
            />

            {/* <CustomSelect
              name="isSubmitted"
              label="Submission Status"
              value={formData.isSubmitted.toString()}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isSubmitted: e.target.value === "true",
                }))
              }
              options={[
                { label: "Submitted", value: "true" },
                { label: "Not Submitted", value: "false" },
              ]}
            /> */}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-neutral-400 font-semibold text-sm">
              Add Attachment
            </label>
            <div className="flex gap-3">
              <DocumentUpload
                value={formData.attachment}
                onUploadComplete={(url) =>
                  setFormData((prev) => ({ ...prev, attachment: url }))
                }
                showDropzone={false}
                buttonColor="bg-primary-800"
                buttonText="Choose file"
              />

              {id && !formData.attachment ? (
                <CustomButton
                  color="bg-primary-500"
                  text="No attachment found"
                  disabled
                />
              ) : (
                <>
                  <DocumentUpload
                    value={formData.attachment}
                    onUploadComplete={(url) =>
                      setFormData((prev) => ({ ...prev, attachment: url }))
                    }
                    showButton={false}
                    borderStyle="solid"
                    className="w-48 h-32"
                  />
                </>
              )}
            </div>
          </div>

          <CustomInput
            name="assignmentDescription"
            label="Assignment Description*"
            type="text"
            placeholder="Type here...."
            value={formData.assignmentDescription}
            rows={6}
            isTextarea
            onChange={handleInputChange}
            inputClassName="w-full"
          />
        </div>

        <div className="flex justify-end items-end">
          <CustomButton
            text={isSubmitting ? "Sending..." : "Send Now"}
            disabled={isSubmitting}
            color="bg-primary-500"
            onClick={handleSubmit}
          />
        </div>
      </form>
    </div>
  );
}
